import { useContext, memo } from 'react';

import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';
import { useTracker } from '@openclassrooms/data-connect-web/tracking-events';
import { translate } from '@openclassrooms/translations';
import Hyperlink from '@classify/react-core-components/Hyperlink';
import Feedback from '@mui/icons-material/Feedback';
import { RichContent } from '@openclassrooms/ui-web/rich-content';
import { styled } from '@classify/react-core-components/styles';
import Stack from '@classify/react-core-components/Stack';
import Skeleton from '@classify/react-core-components/Skeleton';

import BottomBanner from 'Modules/courses/components/BottomBanner';

import CourseChapterContext from '../../contexts/courseChapterContext';
import CourseContext from '../../contexts/courseContext';
import BannerWrapper from '../TrainingMerchandising/BannerWrapper/BannerWrapper';

interface Props {
  courseId: number;
  chapterId: number;
}

const InformationalBannerWrapper = styled('div')(({ theme: { spacing, breakpoints } }) => ({
  margin: spacing(2, 0, 0),
  [breakpoints.up('md')]: {
    margin: spacing(4, 0, 0),
  },
}));

const ContentSkeleton = () => (
  <>
    <Skeleton variant="rectangular" height={350} sx={{ mt: 2 }} />

    <Skeleton variant="text" sx={{ my: 2 }} width={175} />

    <Skeleton variant="rectangular" height={150} />

    <Skeleton variant="rectangular" height={100} />
  </>
);

const ChapterContentWrapper = memo(({ courseId, chapterId }: Props) => {
  const { userId } = useCurrentUserContext();
  const { chapterContent, currentChapter } = useContext(CourseChapterContext);
  const { course, currentUser } = useContext(CourseContext);
  const track = useTracker();

  if (!chapterContent || !currentChapter) return <ContentSkeleton />;

  const { title: currentChapterTitle } = currentChapter;

  return (
    <>
      <RichContent
        content={chapterContent}
        shouldZoomIntoImage
        shouldLockMediaContent={!currentUser}
        accessibleVideoTitle={translate('courses.accessibility_labels.chapter.video_title', {
          chapterTitle: currentChapterTitle,
        })}
        expandableContentLabel={translate('codevolve.title')}
      />

      <Stack alignItems="center" sx={{ mt: 2 }} direction="row">
        <Feedback sx={{ marginRight: 2, color: 'brand.main' }} />
        <Hyperlink
          target="_blank"
          href={`https://openclassrooms.tfaforms.net/4984477?tfa_19=${userId}&tfa_13=${courseId}&tfa_15=${chapterId}&tfa_17=${course.category}`}
          onClick={() => {
            track('Clicked Suggestion Link From Course', {
              data: { userId, courseId, chapterId },
            });
          }}
        >
          {translate('chapterpage.feedback.button')}
        </Hyperlink>
      </Stack>

      <BannerWrapper>
        <InformationalBannerWrapper>
          <BottomBanner ctaDataEvent="Navigation|Redirection::Internal|Chapter Apply" />
        </InformationalBannerWrapper>
      </BannerWrapper>
    </>
  );
});

export default ChapterContentWrapper;
